import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  message,
  Popconfirm,
  Space,
  Typography,
  Spin,
  Tooltip,
} from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, PaperClipOutlined, LinkOutlined, DownOutlined, UploadOutlined, HistoryOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/store/authStore';
import {
  createDesignDocument,
  updateDesignDocument,
  deleteDesignDocument,
  getDesignDocumentVersions,
  getDesignDocumentVersionDetail,
  deleteDesignDocumentVersion,
} from '@/api/requirements';
import { parseFeishuTitles } from '@/api/feishu';
import { getDownloadUrl, proxyDownloadFile } from '@/api/storage';
import { useRequirementStore } from '@/store/requirementStore';
import DesignDocumentAttachments from './DesignDocumentAttachments';
import DesignDocumentUpdateModal from './DesignDocumentUpdateModal';
import DesignDocumentVersionEditModal from './DesignDocumentVersionEditModal';
import DesignDocumentLogModal from './DesignDocumentLogModal';
import { formatDate } from '@/utils';
import type {
  DesignDocument,
  DesignDocumentFeatures,
  DesignDocumentRequirementAttribute,
  DesignDocumentVersion,
} from '@/types';

const { TextArea } = Input;

interface DesignDocumentTableProps {
  projectId: number;
}

const DesignDocumentTable: React.FC<DesignDocumentTableProps> = ({ projectId }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDocument, setEditingDocument] = useState<DesignDocument | null>(null);
  const [attachmentModalVisible, setAttachmentModalVisible] = useState(false);
  const [currentDocument, setCurrentDocument] = useState<DesignDocument | null>(null);
  const [form] = Form.useForm();

  // Version management state
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
  const [versionsMap, setVersionsMap] = useState<Record<number, DesignDocumentVersion[]>>({});
  const [versionsLoading, setVersionsLoading] = useState<Record<number, boolean>>({});
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [currentEditDocument, setCurrentEditDocument] = useState<DesignDocument | null>(null);
  const [versionsDetailMap, setVersionsDetailMap] = useState<Record<string, any>>({});
  const [logModalVisible, setLogModalVisible] = useState(false);

  // Version edit modal state
  const [versionEditModalVisible, setVersionEditModalVisible] = useState(false);
  const [versionEditDocumentId, setVersionEditDocumentId] = useState<number | null>(null);
  const [versionEditVersionId, setVersionEditVersionId] = useState<number | null>(null);

  // Feishu titles state
  const [feishuTitlesMap, setFeishuTitlesMap] = useState<Record<string, string>>({});
  const [titlesLoading, setTitlesLoading] = useState(false);
  const requestedUrlsRef = useRef<Set<string>>(new Set()); // 跟踪已请求的URL，避免重复请求

  // 主动刷新指定URL列表的飞书标题（提交后调用，强制重新获取）
  const fetchTitlesForUrls = useCallback(async (urls: string[]) => {
    const feishuUrls = urls.filter(url => url.includes('feishu.cn/'));
    if (feishuUrls.length === 0) return;
    // 从已请求集合中移除，确保可以重新获取（例如用户修改了URL）
    feishuUrls.forEach(url => requestedUrlsRef.current.delete(url));
    feishuUrls.forEach(url => requestedUrlsRef.current.add(url));
    try {
      const response = await parseFeishuTitles(feishuUrls);
      const newTitles: Record<string, string> = {};
      response.results.forEach((result: any) => {
        if (result.success && result.title) {
          newTitles[result.url] = result.title;
        }
      });
      if (Object.keys(newTitles).length > 0) {
        setFeishuTitlesMap(prev => ({ ...prev, ...newTitles }));
      }
    } catch (error) {
      console.error('Failed to fetch Feishu titles:', error);
    }
  }, []);

  const { user } = useAuthStore();
  const { designDocuments, designDocumentsLoading, fetchDesignDocuments } =
    useRequirementStore();

  const isInternal = user?.user_type === 'internal';

  // 使用 useMemo 来稳定 designDocuments 的引用，避免无限重渲染
  // 只有当文档ID列表变化时才认为数据变化
  const designDocumentsIds = useMemo(
    () => designDocuments.map(doc => doc.id).join(','),
    [designDocuments]
  );

  useEffect(() => {
    if (projectId && projectId > 0) {
      fetchDesignDocuments(projectId);
    }
  }, [projectId, fetchDesignDocuments]);

  // 预加载所有文档的版本列表（只加载列表，不加载详情）
  useEffect(() => {
    if (projectId && projectId > 0 && designDocuments.length > 0 && designDocumentsIds) {
      const preloadVersions = async () => {
        for (const doc of designDocuments) {
          if (!versionsMap[doc.id]) {
            try {
              const versions = await getDesignDocumentVersions(projectId, doc.id);
              setVersionsMap((prev) => ({ ...prev, [doc.id]: versions }));

              // 预加载最新版本的详情（用于默认行显示）
              if (versions.length > 0) {
                const latestVersion = versions[0];
                const detailKey = `${doc.id}-${latestVersion.id}`;
                if (!versionsDetailMap[detailKey]) {
                  try {
                    const detail = await getDesignDocumentVersionDetail(projectId, doc.id, latestVersion.id);
                    setVersionsDetailMap((prev) => ({ ...prev, [detailKey]: detail }));
                  } catch (error) {
                    console.error(`Failed to preload version detail ${latestVersion.id}:`, error);
                  }
                }
              }
            } catch (error) {
              console.error('Failed to preload versions:', error);
            }
          }
        }
      };

      preloadVersions();
    }
    // 注意：不在依赖数组中包含 versionsMap 和 versionsDetailMap，避免无限循环
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designDocumentsIds, projectId]);

  // 当行展开时，加载该行的所有版本详情
  useEffect(() => {
    if (expandedRowKeys.length > 0) {
      const loadDetailsForExpandedRows = async () => {
        for (const docId of expandedRowKeys) {
          const versions = versionsMap[docId];
          if (versions && versions.length > 0) {
            // 加载所有版本的详情（不仅仅是最新版本）
            for (const version of versions) {
              const detailKey = `${docId}-${version.id}`;
              if (!versionsDetailMap[detailKey]) {
                try {
                  const detail = await getDesignDocumentVersionDetail(projectId, docId, version.id);
                  setVersionsDetailMap((prev) => ({ ...prev, [detailKey]: detail }));
                } catch (error) {
                  console.error(`Failed to load version detail ${version.id}:`, error);
                }
              }
            }
          }
        }
      };

      loadDetailsForExpandedRows();
    }
  }, [expandedRowKeys, versionsMap, projectId]);

  // 当主表数据更新时，同步更新 currentDocument（用于附件模态框）
  useEffect(() => {
    if (attachmentModalVisible && currentDocument) {
      const updatedDoc = designDocuments.find(d => d.id === currentDocument.id);
      if (updatedDoc) {
        setCurrentDocument(updatedDoc);
      }
    }
  }, [designDocuments, attachmentModalVisible, currentDocument?.id]);

  // 加载主记录的飞书文档标题
  useEffect(() => {
    if (projectId && projectId > 0 && designDocuments.length > 0 && designDocumentsIds) {
      const loadMainDocumentTitles = async () => {
        // 提取主记录的飞书URL（支持wiki/docx/sheets/mindnote等所有格式）
        const allUrls: string[] = [];
        designDocuments.forEach(doc => {
          doc.documents.forEach(url => {
            if (url.includes('feishu.cn/')) {
              allUrls.push(url);
            }
          });
        });

        if (allUrls.length === 0) return;

        // 去重并过滤已请求的URL
        const uniqueUrls = Array.from(new Set(allUrls)).filter(
          url => !requestedUrlsRef.current.has(url)
        );
        if (uniqueUrls.length === 0) return;

        // 标记为已请求（在请求前标记，避免重复请求）
        uniqueUrls.forEach(url => requestedUrlsRef.current.add(url));

        setTitlesLoading(true);
        try {
          const response = await parseFeishuTitles(uniqueUrls);

          // 增量更新标题映射（保留已有的标题）
          const newTitles: Record<string, string> = {};
          response.results.forEach(result => {
            if (result.success && result.title) {
              newTitles[result.url] = result.title;
            }
          });

          setFeishuTitlesMap(prev => ({ ...prev, ...newTitles }));
        } catch (error) {
          console.error('Failed to load main document Feishu titles:', error);
        } finally {
          setTitlesLoading(false);
        }
      };

      loadMainDocumentTitles();
    }
    // 注意：不依赖 versionsDetailMap，避免无限重渲染
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designDocumentsIds, projectId]);

  // 加载版本快照的飞书文档标题（增量加载）
  useEffect(() => {
    const loadVersionTitles = async () => {
      // 提取版本快照的飞书URL（支持所有格式）
      const versionUrls: string[] = [];
      Object.values(versionsDetailMap).forEach((versionDetail: any) => {
        versionDetail.documents?.forEach((url: string) => {
          if (url.includes('feishu.cn/') && !requestedUrlsRef.current.has(url)) {
            versionUrls.push(url);
          }
        });
      });

      if (versionUrls.length === 0) return;

      // 去重
      const uniqueUrls = Array.from(new Set(versionUrls));

      // 标记为已请求
      uniqueUrls.forEach(url => requestedUrlsRef.current.add(url));

      try {
        const response = await parseFeishuTitles(uniqueUrls);

        // 增量更新标题映射
        const newTitles: Record<string, string> = {};
        response.results.forEach(result => {
          if (result.success && result.title) {
            newTitles[result.url] = result.title;
          }
        });

        if (Object.keys(newTitles).length > 0) {
          setFeishuTitlesMap(prev => ({ ...prev, ...newTitles }));
        }
      } catch (error) {
        console.error('Failed to load version Feishu titles:', error);
      }
    };

    // 仅当有新的版本详情加载时才执行
    if (Object.keys(versionsDetailMap).length > 0) {
      loadVersionTitles();
    }
    // 依赖 versionsDetailMap，但通过 requestedUrlsRef 避免重复请求相同的URL
  }, [versionsDetailMap]);

  const handleCreate = () => {
    setEditingDocument(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: DesignDocument) => {
    setEditingDocument(record);
    form.setFieldsValue({
      file_description: record.file_description,
      features: record.features,
      requirement_attribute: record.requirement_attribute,
      documents: record.documents.join('\n'),
      remarks: record.remarks,
      user_version: record.user_version || '',
    });
    setModalVisible(true);
  };

  const handleDelete = async (record: DesignDocument) => {
    try {
      await deleteDesignDocument(projectId, record.id);
      message.success('删除成功');
      fetchDesignDocuments(projectId);
    } catch (error: any) {
      console.error('Design document delete error:', error);
      let errorMsg = '删除失败';

      if (error?.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error?.message) {
        errorMsg = error.message;
      }

      message.error(errorMsg);
    }
  };

  const handleManageAttachments = (record: DesignDocument) => {
    setCurrentDocument(record);
    setAttachmentModalVisible(true);
  };

  // Version management handlers
  const loadVersions = async (documentId: number, forceRefresh = false) => {
    const versions = versionsMap[documentId];

    // 如果版本列表还没加载或强制刷新，先加载列表
    if (!versions || forceRefresh) {
      setVersionsLoading({ ...versionsLoading, [documentId]: true });
      try {
        const loadedVersions = await getDesignDocumentVersions(projectId, documentId);
        setVersionsMap({ ...versionsMap, [documentId]: loadedVersions });

        // 如果版本数 <= 1，自动收起
        if (loadedVersions.length <= 1) {
          setExpandedRowKeys((prev) => prev.filter((key) => key !== documentId));
          return;
        }

        // 清除该文档所有版本的详情缓存（强制刷新时）
        if (forceRefresh) {
          setVersionsDetailMap((prev) => {
            const newMap = { ...prev };
            Object.keys(newMap).forEach((key) => {
              if (key.startsWith(`${documentId}-`)) {
                delete newMap[key];
              }
            });
            return newMap;
          });
        }

        // Load version details
        await loadVersionDetails(documentId, loadedVersions);
      } catch (error) {
        console.error('Failed to load versions:', error);
        message.error('加载版本历史失败');
      } finally {
        setVersionsLoading({ ...versionsLoading, [documentId]: false });
      }
    } else {
      // 版本列表已经预加载，直接加载详情
      await loadVersionDetails(documentId, versions);
    }
  };

  const loadVersionDetails = async (documentId: number, versions: DesignDocumentVersion[]) => {
    // Load version details for all versions to get attachments
    for (const version of versions) {
      const detailKey = `${documentId}-${version.id}`;
      if (!versionsDetailMap[detailKey]) {
        try {
          const detail = await getDesignDocumentVersionDetail(projectId, documentId, version.id);
          setVersionsDetailMap((prev) => ({ ...prev, [detailKey]: detail }));
        } catch (error) {
          console.error(`Failed to load version detail ${version.id}:`, error);
        }
      }
    }
  };

  const handleExpand = (expanded: boolean, record: DesignDocument) => {
    if (expanded) {
      loadVersions(record.id);
    }
  };

  const handleUpdate = (record: DesignDocument) => {
    setCurrentEditDocument(record);
    setUpdateModalVisible(true);
  };

  const handleDeleteVersion = async (documentId: number, versionId: number, latestVersionId: number) => {
    if (versionId === latestVersionId) {
      message.error('不能删除最新版本');
      return;
    }

    try {
      await deleteDesignDocumentVersion(projectId, documentId, versionId);
      message.success('版本已删除');

      // 刷新主表数据
      await fetchDesignDocuments(projectId);

      // 强制重新加载版本列表（会自动清除缓存并从API重新加载）
      await loadVersions(documentId, true);
    } catch (error: any) {
      console.error('Delete version failed:', error);
      let errorMsg = '删除失败';

      if (error?.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error?.message) {
        errorMsg = error.message;
      }

      message.error(errorMsg);
    }
  };

  const handleEditVersion = (documentId: number, versionId: number) => {
    setVersionEditDocumentId(documentId);
    setVersionEditVersionId(versionId);
    setVersionEditModalVisible(true);
  };

  const handleVersionEditSuccess = async () => {
    const documentId = versionEditDocumentId;
    if (!documentId) return;

    // 刷新主表数据
    await fetchDesignDocuments(projectId);

    // 清除该文档的版本列表和版本详情缓存，强制重新加载
    setVersionsMap((prev) => {
      const newMap = { ...prev };
      delete newMap[documentId];
      return newMap;
    });

    const detailKeyPrefix = `${documentId}-`;
    setVersionsDetailMap((prev) => {
      const newMap = { ...prev };
      Object.keys(newMap).forEach((key) => {
        if (key.startsWith(detailKeyPrefix)) {
          delete newMap[key];
        }
      });
      return newMap;
    });

    // 保持展开并重新加载版本列表
    setExpandedRowKeys([documentId]);
    await loadVersions(documentId, true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const documentUrls = parseDocumentUrls(values.documents || '');

      const payload = {
        file_description: values.file_description,
        features: values.features,
        requirement_attribute: values.requirement_attribute,
        documents: documentUrls,
        remarks: values.remarks || '',
        user_version: values.user_version || '',
      };

      console.log('Submitting design document:', { projectId, payload, editingDocument: !!editingDocument });

      if (editingDocument) {
        const editingDocId = editingDocument.id;
        await updateDesignDocument(projectId, editingDocId, payload);
        message.success('更新成功');

        // 编辑完成：清除该文档的所有版本缓存（包括列表和详情）
        // 这样 preload useEffect 会重新加载所有数据
        setVersionsMap((prev) => {
          const newMap = { ...prev };
          delete newMap[editingDocId];
          return newMap;
        });

        // 清除所有该文档相关的版本详情缓存
        const detailKeyPrefix = `${editingDocId}-`;
        setVersionsDetailMap((prev) => {
          const newMap = { ...prev };
          Object.keys(newMap).forEach((key) => {
            if (key.startsWith(detailKeyPrefix)) {
              delete newMap[key];
            }
          });
          return newMap;
        });
      } else {
        await createDesignDocument(projectId, payload);
        message.success('创建成功');
      }

      console.log('Design document operation succeeded');
      setModalVisible(false);
      await fetchDesignDocuments(projectId);
      // 提交后立即刷新该文档的飞书标题，无需重新加载页面
      fetchTitlesForUrls(documentUrls);
    } catch (error: any) {
      console.error('Design document operation error:', error);
      let errorMsg = '操作失败';

      if (error?.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error?.message) {
        errorMsg = error.message;
      }

      console.log('Error message:', errorMsg);
      message.error(errorMsg);
    }
  };

  // 格式化文件大小
  const formatFileSize = (bytes: number): string => {
    if (!bytes || isNaN(bytes)) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // 处理附件下载
  const handleAttachmentDownload = async (fileUrl: string, fileName: string) => {
    if (user?.user_type === 'external') {
      proxyDownloadFile(fileUrl, fileName);
    } else {
      try {
        const { url } = await getDownloadUrl(fileUrl);
        window.open(url, '_blank');
      } catch (error) {
        console.error('Download failed:', error);
        message.error(`下载失败: ${fileName}`);
      }
    }
  };

  const columns = [
    {
      title: '文件编号',
      dataIndex: 'file_number',
      key: 'file_number',
      width: 80,
      sorter: (a: DesignDocument, b: DesignDocument) => a.file_number - b.file_number,
    },
    {
      title: '文件描述',
      dataIndex: 'file_description',
      key: 'file_description',
      width: 300,
      render: (text: string) => <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</div>,
    },
    {
      title: 'Features',
      dataIndex: 'features',
      key: 'features',
      width: 120,
      render: (features: DesignDocumentFeatures) => {
        const colorMap: Record<DesignDocumentFeatures, string> = {
          MCU: 'blue',
          SOC: 'green',
          'MCU&SOC': 'orange',
        };
        return <Tag color={colorMap[features]}>{features}</Tag>;
      },
    },
    {
      title: '需求属性',
      dataIndex: 'requirement_attribute',
      key: 'requirement_attribute',
      width: 100,
      render: (attr: DesignDocumentRequirementAttribute) => {
        const color = attr === '必选' ? 'red' : 'default';
        return <Tag color={color}>{attr}</Tag>;
      },
    },
    {
      title: '文档',
      dataIndex: 'documents',
      key: 'documents',
      width: 250,
      render: (_: string[], record: DesignDocument) => {
        // 最新版本始终显示主记录的最新数据
        const documentUrls = record.documents || [];
        const attachments = record.attachments || [];

        if (documentUrls.length === 0 && attachments.length === 0) {
          return <span style={{ color: '#999' }}>无</span>;
        }

        return (
          <Space orientation="vertical" size="small" style={{ width: '100%' }}>
            {/* 外部文档URLs - 显示主记录的最新文档链接 */}
            {documentUrls.map((url: string, idx: number) => {
              // 优先使用飞书解析的标题（支持所有飞书文档格式）
              const isFeishuUrl = url.includes('feishu.cn/');
              const displayTitle = feishuTitlesMap[url] || `设计文档${idx + 1}`;

              return (
                <a
                  key={`url-${idx}`}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#1890ff' }}
                  title={url}
                >
                  <LinkOutlined style={{ fontSize: 12 }} />
                  <span>{displayTitle}</span>
                  {/* 加载中指示器 */}
                  {titlesLoading && isFeishuUrl && !feishuTitlesMap[url] && (
                    <Spin size="small" style={{ marginLeft: 4 }} />
                  )}
                </a>
              );
            })}

            {/* 上传的附件 - 显示主记录的最新附件 */}
            {attachments.map((att: any) => (
              <a
                key={`att-${att.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  handleAttachmentDownload(att.file_url, att.file_name);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  color: '#1890ff',
                  cursor: 'pointer',
                }}
                title={`${att.file_name} (${formatFileSize(att.file_size)})`}
              >
                <PaperClipOutlined style={{ fontSize: 12 }} />
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {att.file_name}
                </span>
              </a>
            ))}
          </Space>
        );
      },
    },
    {
      title: '版本',
      dataIndex: 'user_version',
      key: 'user_version',
      width: 100,
      render: (text: string) => text || <span style={{ color: '#999' }}>-</span>,
    },
    {
      title: '备注',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 200,
      render: (text: string) => <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text || '-'}</div>,
    },
    {
      title: '更新人',
      dataIndex: 'updated_by_name',
      key: 'updated_by_name',
      width: 100,
      render: (text: string) => text || '-',
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      render: (dateString: string) => formatDate(dateString),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: DesignDocument) => (
        <Space size="small">
          {/* 编辑 */}
          {isInternal && (
            <Tooltip title="编辑文件">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
          )}

          {/* 附件 */}
          <Tooltip title={isInternal ? "上传附件" : "下载附件"}>
            <Button
              type="text"
              size="small"
              icon={<PaperClipOutlined />}
              onClick={() => handleManageAttachments(record)}
            />
          </Tooltip>

          {/* 发布新版本 */}
          {isInternal && (
            <Tooltip title="发布新版本">
              <Button
                type="text"
                size="small"
                icon={<UploadOutlined />}
                onClick={() => handleUpdate(record)}
              />
            </Tooltip>
          )}

          {/* 删除 */}
          {isInternal && (
            <Popconfirm
              title="确定删除这条设计文档吗？"
              onConfirm={() => handleDelete(record)}
            >
              <Tooltip title="删除">
                <Button type="text" danger size="small" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {!projectId || projectId <= 0 ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
          无效的项目ID，无法加载设计文档
        </div>
      ) : (
        <>
          {!isInternal && (
            <div
              style={{
                marginBottom: 16,
                padding: '8px 16px',
                background: '#f0f0f0',
                borderRadius: 4,
              }}
            >
              <span>您是外部用户，仅可查看设计文档，无法编辑或删除</span>
            </div>
          )}

          {isInternal && (
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                  新增设计文档
                </Button>
                <Button icon={<HistoryOutlined />} onClick={() => setLogModalVisible(true)}>
                  操作日志
                </Button>
              </Space>
            </div>
          )}

          <Table
            columns={columns}
            dataSource={designDocuments}
            loading={designDocumentsLoading}
            rowKey="id"
            scroll={{ x: 1200 }}
            expandable={{
              expandedRowKeys,
              onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as number[]),
              onExpand: handleExpand,
              expandIcon: ({ expanded, onExpand, record }: any) => {
                const versions = versionsMap[record.id];

                // 如果已经加载过且版本数 <= 1，不显示展开符号
                if (versions && versions.length <= 1) {
                  return null;
                }

                // 否则显示展开符号（包括还没加载的情况）
                return (
                  <DownOutlined
                    rotate={expanded ? 180 : 0}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      onExpand(record, e);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                );
              },
              expandedRowRender: (record) => {
                const versions = versionsMap[record.id] || [];
                const loading = versionsLoading[record.id];

                // 版本数<=1时，不显示任何内容
                if (versions.length <= 1) {
                  return null;
                }

                if (loading) {
                  return <Spin size="small" />;
                }

                if (versions.length === 0) {
                  return <div style={{ padding: 12, color: '#999' }}>暂无历史版本</div>;
                }

                return (
                  <Table
                    dataSource={versions}
                    rowKey="id"
                    size="small"
                    pagination={false}
                    columns={[
                      {
                        title: '版本标识',
                        dataIndex: 'version_number',
                        width: 80,
                        render: (num: number) => `V${num}`
                      },
                      {
                        title: '版本',
                        dataIndex: 'user_version',
                        width: 100,
                        render: (text: string) => text || <span style={{ color: '#999' }}>-</span>,
                      },
                      {
                        title: '更新说明',
                        dataIndex: 'version_remarks',
                        width: 200,
                      },
                      {
                        title: '文档和附件',
                        width: 300,
                        render: (_: any, versionRecord: DesignDocumentVersion) => {
                          // 判断是否为最新版本
                          const latestVersion = versions && versions.length > 0 ? versions[0] : null;
                          const isLatestVersion = latestVersion && versionRecord.id === latestVersion.id;

                          let documentUrls: string[] = [];
                          let attachments: any[] = [];

                          if (isLatestVersion) {
                            // 最新版本显示主记录的最新数据
                            documentUrls = record.documents || [];
                            attachments = record.attachments || [];
                          } else {
                            // 历史版本显示版本快照
                            const detailKey = `${record.id}-${versionRecord.id}`;
                            const versionDetail = versionsDetailMap[detailKey];
                            if (!versionDetail) {
                              return <span style={{ color: '#999' }}>-</span>;
                            }
                            documentUrls = versionDetail.documents || [];
                            attachments = versionDetail.attachments || [];
                          }

                          if (documentUrls.length === 0 && attachments.length === 0) {
                            return <span style={{ color: '#999' }}>无</span>;
                          }

                          return (
                            <Space orientation="vertical" size="small">
                              {/* 文档URL - 显示飞书标题或降级 */}
                              {documentUrls.map((url: string, idx: number) => {
                                const displayTitle = feishuTitlesMap[url] || `设计文档${idx + 1}`;

                                return (
                                  <a
                                    key={`url-${idx}`}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontSize: 12, display: 'block' }}
                                    title={url}
                                  >
                                    <LinkOutlined style={{ marginRight: 4, fontSize: 10 }} />
                                    {displayTitle}
                                  </a>
                                );
                              })}
                              {/* 附件 */}
                              {attachments.map((att: any) => (
                                <a
                                  key={`att-${att.id}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleAttachmentDownload(att.file_url, att.file_name);
                                  }}
                                  style={{
                                    fontSize: 12,
                                    color: '#1890ff',
                                    cursor: 'pointer',
                                    display: 'block',
                                  }}
                                  title={`${att.file_name} (${formatFileSize(att.file_size)})`}
                                >
                                  <PaperClipOutlined style={{ marginRight: 4, fontSize: 10 }} />
                                  {att.file_name}
                                </a>
                              ))}
                            </Space>
                          );
                        },
                      },
                      {
                        title: '更新人',
                        dataIndex: 'updated_by_name',
                        width: 100,
                      },
                      {
                        title: '更新时间',
                        dataIndex: 'created_at',
                        width: 160,
                        render: (date: string, versionRecord: DesignDocumentVersion) => {
                          // 最新版本显示主记录的最新更新时间，其他版本显示版本创建时间
                          const latestVersion = versions && versions.length > 0 ? versions[0] : null;
                          const isLatestVersion = latestVersion && versionRecord.id === latestVersion.id;
                          const displayDate = isLatestVersion ? record.updated_at : date;
                          return formatDate(displayDate);
                        },
                      },
                      {
                        title: '操作',
                        width: 110,
                        render: (_: any, versionRecord: DesignDocumentVersion) => {
                          const latestVersion = versions && versions.length > 0 ? versions[0] : null;
                          const isLatestVersion = latestVersion && versionRecord.id === latestVersion.id;

                          if (isLatestVersion || !isInternal) {
                            return null;
                          }

                          return (
                            <Space size={4}>
                              <Tooltip title="编辑版本">
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => handleEditVersion(record.id, versionRecord.id)}
                                />
                              </Tooltip>
                              <Popconfirm
                                title="确定删除这个版本吗？"
                                onConfirm={() => handleDeleteVersion(record.id, versionRecord.id, latestVersion!.id)}
                              >
                                <Tooltip title="删除版本">
                                  <Button type="text" danger size="small" icon={<DeleteOutlined />} />
                                </Tooltip>
                              </Popconfirm>
                            </Space>
                          );
                        },
                      },
                    ]}
                  />
                );
              },
            }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条`,
            }}
          />

          <Modal
            title={editingDocument ? '编辑设计文档' : '新增设计文档'}
            open={modalVisible}
            onOk={handleSubmit}
            onCancel={() => setModalVisible(false)}
            width={600}
            okText="提交"
            cancelText="取消"
          >
            <Form form={form} layout="vertical">
              <Form.Item
                label="文件描述"
                name="file_description"
                rules={[
                  { required: true, message: '请输入文件描述' },
                  { min: 5, message: '至少5个字符' },
                  { max: 1000, message: '最多1000个字符' },
                ]}
              >
                <TextArea
                  rows={3}
                  placeholder="请输入文件描述"
                  showCount
                  maxLength={1000}
                />
              </Form.Item>

              <Form.Item
                label="Features"
                name="features"
                rules={[{ required: true, message: '请选择Features' }]}
              >
                <Select placeholder="请选择">
                  <Select.Option value="MCU">MCU</Select.Option>
                  <Select.Option value="SOC">SOC</Select.Option>
                  <Select.Option value="MCU&SOC">MCU&SOC</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="需求属性"
                name="requirement_attribute"
                rules={[{ required: true, message: '请选择需求属性' }]}
              >
                <Select placeholder="请选择">
                  <Select.Option value="必选">必选</Select.Option>
                  <Select.Option value="可选">可选</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="文档链接"
                name="documents"
                help="每行一个URL链接，最多10个"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();
                      const urls = parseDocumentUrls(value);
                      if (urls.length > 10) {
                        return Promise.reject(new Error('最多支持10个文档链接'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <TextArea
                  rows={5}
                  placeholder={
                    '每行一个URL链接\nhttps://example.com/doc1\nhttps://example.com/doc2'
                  }
                />
              </Form.Item>

              <Form.Item label="备注" name="remarks">
                <TextArea rows={2} placeholder="可选" />
              </Form.Item>

              <Form.Item
                label="版本"
                name="user_version"
                help="可选，用于标识版本号（如V1.0.0, V2.1.3等）"
                rules={[{ max: 50, message: '版本号最多50个字符' }]}
              >
                <Input
                  placeholder="请输入版本号（可选）"
                  maxLength={50}
                />
              </Form.Item>
            </Form>
          </Modal>

          {/* Attachment management modal */}
          <Modal
            title={currentDocument ? `附件管理 - 文件${currentDocument.file_number}` : '附件管理'}
            open={attachmentModalVisible}
            onCancel={() => setAttachmentModalVisible(false)}
            footer={null}
            width={700}
          >
            {currentDocument && (
              <>
                <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                  <Typography.Text strong>文件描述：</Typography.Text>
                  <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                    {currentDocument.file_description}
                  </div>
                </div>
                <DesignDocumentAttachments
                  projectId={projectId}
                  documentId={currentDocument.id}
                  onAttachmentsChange={() => {
                    // 刷新主表数据
                    fetchDesignDocuments(projectId);
                  }}
                />
              </>
            )}
          </Modal>

          {/* Update document modal */}
          <DesignDocumentUpdateModal
            visible={updateModalVisible}
            document={currentEditDocument}
            projectId={projectId}
            onCancel={() => {
              setUpdateModalVisible(false);
              setCurrentEditDocument(null);
            }}
            onSuccess={(updatedUrls: string[]) => {
              fetchDesignDocuments(projectId);
              // 提交后立即刷新该文档的飞书标题
              fetchTitlesForUrls(updatedUrls);

              // 清除该文档的版本列表和版本详情缓存，强制重新加载
              if (currentEditDocument) {
                setVersionsMap((prev) => {
                  const newMap = { ...prev };
                  delete newMap[currentEditDocument.id];
                  return newMap;
                });

                const detailKeyPrefix = `${currentEditDocument.id}-`;
                setVersionsDetailMap((prev) => {
                  const newMap = { ...prev };
                  Object.keys(newMap).forEach((key) => {
                    if (key.startsWith(detailKeyPrefix)) {
                      delete newMap[key];
                    }
                  });
                  return newMap;
                });
              }

              // Optionally expand the row to show new version
              if (currentEditDocument) {
                setExpandedRowKeys([currentEditDocument.id]);
                loadVersions(currentEditDocument.id, true);
              }
            }}
          />

          <DesignDocumentVersionEditModal
            visible={versionEditModalVisible}
            projectId={projectId}
            documentId={versionEditDocumentId ?? 0}
            versionId={versionEditVersionId}
            onCancel={() => {
              setVersionEditModalVisible(false);
              setVersionEditDocumentId(null);
              setVersionEditVersionId(null);
            }}
            onSuccess={handleVersionEditSuccess}
          />

          <DesignDocumentLogModal
            open={logModalVisible}
            onCancel={() => setLogModalVisible(false)}
            projectId={projectId}
          />
        </>
      )}
    </div>
  );
};

function parseDocumentUrls(text: string): string[] {
  return text
    .split('\n')
    .map((url) => url.trim())
    .filter((url) => url.length > 0);
}

export default DesignDocumentTable;
